// syncConfig.js — defines which Supabase tables are mirrored locally in PGlite.
//
// Each entry has:
//   name         — Supabase table name (same name used in PGlite)
//   indexedCols  — columns pulled out of _data into top-level indexed columns
//                  (used for efficient WHERE / ORDER BY in local queries)
//   batchSize    — rows per Supabase .range() page during pull
//   maxAgeDays   — if set, only pull rows updated within this many days
//                  (keeps high-volume tables like call_log from pulling all history)
//
// _data JSONB always contains the FULL row from Supabase, so no column is ever lost.
// id and updated_at are always stored as top-level columns regardless of indexedCols.

export const SYNC_TABLES = [
  {
    name: 'contacts',
    indexedCols: ['first_name', 'last_name', 'priority_tier', 'is_client', 'organization_id', 'company_id'],
    batchSize: 500,
  },
  {
    name: 'companies',
    indexedCols: ['name', 'entity_id'],
    batchSize: 200,
  },
  {
    name: 'deals',
    indexedCols: ['title', 'status', 'deal_type', 'company_id'],
    batchSize: 200,
  },
  {
    name: 'projects',
    indexedCols: ['title', 'status', 'format', 'company_id'],
    batchSize: 200,
  },
  {
    name: 'properties',
    indexedCols: ['title', 'status', 'track', 'company_id'],
    batchSize: 200,
  },
  {
    name: 'opportunities',
    indexedCols: ['title', 'status', 'type', 'company_id'],
    batchSize: 200,
  },
  {
    name: 'tasks',
    indexedCols: ['title', 'status', 'quadrant', 'due_date', 'company_id'],
    batchSize: 200,
  },
  {
    name: 'call_log',
    indexedCols: ['contact_id', 'contact_date', 'call_type', 'company_id'],
    batchSize: 200,
    maxAgeDays: 180,  // only last 6 months — call_log can be very large
    updatedAtCol: 'created_at',  // call_log has no updated_at — use created_at
  },
  {
    name: 'client_records',
    indexedCols: ['contact_id', 'status', 'company_id'],
    batchSize: 200,
  },
  {
    name: 'document_drafts',
    indexedCols: ['title', 'status', 'project_id', 'property_id', 'company_id'],
    batchSize: 100,
  },
  {
    name: 'invoices',
    indexedCols: ['invoice_number', 'status', 'invoice_type', 'client_id', 'deal_id', 'company_id'],
    batchSize: 200,
    maxAgeDays: 365, // limit initial pull to last year
  },
  // invoice_line_items excluded — no updated_at column, can't do incremental sync
  {
    // Inbox triage rows — mirrored so a hung/timed-out Supabase query in the
    // inbox falls through to locally-stored data instead of an empty list.
    name: 'email_intelligence',
    indexedCols: [
      'user_id', 'category', 'status', 'urgency', 'email_type', 'triage_status',
      'is_read', 'is_outbound', 'is_starred', 'sent_at',
      'gmail_thread_id', 'gmail_message_id',
    ],
    batchSize: 200,
    maxAgeDays: 90,  // inbox only needs the recent triage window offline
  },
  {
    // Raw email bodies / thread rows — feeds the sidebar participant view and
    // thread reads when offline. No updated_at column, so sync on sent_at.
    name: 'saved_emails',
    indexedCols: ['gmail_message_id', 'gmail_thread_id', 'sender_email', 'is_outbound', 'sent_at', 'user_id'],
    batchSize: 200,
    maxAgeDays: 90,
    updatedAtCol: 'sent_at',  // saved_emails has no updated_at — sync on sent_at
  },
]

// Sync order matters for foreign keys — parents before children.
// (PGlite doesn't enforce FK constraints but ordering keeps _data consistent.)
export const SYNC_ORDER = [
  'companies',
  'contacts',
  'client_records',
  'properties',
  'projects',
  'opportunities',
  'deals',
  'tasks',
  'call_log',
  'document_drafts',
  'invoices',
  'email_intelligence',
  'saved_emails',
]
